import {getStrings} from '../lang';
import {httpService, IHttpRequestOptions} from '../services/http.service';
import {ICrudDataSource, setMetadata, UKey} from './data-source';
import {fromJson, json, toJson} from './json.mapper';
const S: CommonStrings = getStrings();
export enum CrudMethod {
    CREATE,
    GET,
    REMOVE,
    LIST,
    UPDATE
}

export class HttpCrudParams {
    results: number = 1000;
    startIndex: number = 0;
    groupBy: string = null;
    fields: any = null;
    search: string = null;
    filter: string = null;
    dir: string = 'ASC';
    sort: string = 'name';

    constructor(params: any = {}) {
        Object.assign(this, params);
    }

    public toString(): string {
        return ['dir', 'sort', 'results', 'startIndex', 'groupBy', 'fields', 'search', 'filter']
            .map((key: string) => [key, this[key]].join('='))
            .join('&');
    }
}

export class RestResponseRecord {
    @json()
    errors: any[];
    @json()
    infos: any[];
    @json()
    warnings: any[];
    @json()
    success: boolean;
    @json()
    total: number;
    @json({
        jsonName: 'rows',
        mapper: {
            from: (rows: any[]) => rows.length
        }
    })
    count: number;
}

export abstract class HttpCrudDataSource<T> implements ICrudDataSource<T> {
    protected abstract apiPath: string;

    constructor(protected recordClass: {new (): T}) {}

    protected getUrl(method: CrudMethod, id?: UKey, record?: T): string {
        switch (method) {
            case CrudMethod.GET:
                return `${this.apiPath}/` + id;
            case CrudMethod.REMOVE:
                return `${this.apiPath}/remove`;
            default:
                return this.apiPath;
        }
    }

    async create(record: T, options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<T> {
        return httpService
            .post(this.getUrl(CrudMethod.CREATE, null, record), toJson(record), {...options, signal})
            .then((response: any) => {
                if (response)
                    return setMetadata(
                        fromJson(this.recordClass, response.data.rows[0]),
                        fromJson(RestResponseRecord, response.data)
                    );
            });
    }

    async get(id: UKey, options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<T> {
        return httpService.get(this.getUrl(CrudMethod.GET, id, null), {...options, signal}).then((response: any) => {
            if (response.data.rows.length === 0) {
                throw new Error(S.error);
            }
            return setMetadata(
                fromJson(this.recordClass, response.data.rows[0]),
                fromJson(RestResponseRecord, response.data)
            );
        });
    }

    async remove(ids: UKey[], options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<boolean> {
        return httpService
            .post(
                this.getUrl(CrudMethod.REMOVE),
                ids.map((id: number) => 'ids=' + id).join('&'),
                Object.assign(
                    {},
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                        }
                    },
                    {...options, signal}
                )
            )
            .then((response: any) => {
                return response?.data?.success;
            });
    }

    async list(options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<T[]> {
        return httpService.get(this.getUrl(CrudMethod.LIST), {...options, signal}).then((response: any) => {
            return setMetadata(
                response.data.rows.map((v: any) => fromJson(this.recordClass, v)),
                fromJson(RestResponseRecord, response.data)
            );
        });
    }

    async update(record: T & {id: UKey}, options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<T> {
        return httpService
            .post(this.getUrl(CrudMethod.UPDATE, record.id), toJson(record), {...options, signal})
            .then((response: any) => {
                if (response.data.success) {
                    return setMetadata(
                        fromJson(this.recordClass, response.data.rows[0]),
                        fromJson(RestResponseRecord, response.data)
                    );
                } else {
                    throw response.data.errors;
                }
            });
    }

    async updateDefaults(record: T & {id: UKey}, options: IHttpRequestOptions = {}, signal?: AbortSignal): Promise<T> {
        return httpService
            .post(this.getUrl(CrudMethod.UPDATE, record.id) + '/future', toJson(record), {...options, signal})
            .then((response: any) => {
                if (response.data.success) {
                    return setMetadata(
                        fromJson(this.recordClass, response.data.rows[0]),
                        fromJson(RestResponseRecord, response.data)
                    );
                } else {
                    throw response.data.errors;
                }
            });
    }
}
